<template>
  <sme-card large title="Employee Impact">
    <app-loading v-if="loading" />

    <template v-else>
      <!-- Sentiment Stats Row -->
      <b-row v-if="hasSentimentData" class="mb-4">
        <b-col md="4" class="mb-3 mb-md-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="avgStressedPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel less stressed (avg)"
              background-color="core-1-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="timeGroupedStressedDataset?.values?.length"
              :labels="timeLabels"
              :datasets="timeGroupedStressedDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
        <b-col md="4" class="mb-3 mb-md-0">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="avgControlPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel more in control (avg)"
              background-color="core-2-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="timeGroupedControlDataset?.values?.length"
              :labels="timeLabels"
              :datasets="timeGroupedControlDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
        <b-col md="4">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="avgEmployerPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel positive about employer (avg)"
              background-color="core-3-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="timeGroupedEmployerDataset?.values?.length"
              :labels="timeLabels"
              :datasets="timeGroupedEmployerDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mx-n3 mt-auto mb-n3"
              fill-gradient
            />
          </sme-card>
        </b-col>
      </b-row>

      <!-- Quotes Section -->
      <div v-if="quotes.length" class="quotes-section">
        <h6 class="mb-3">What your employees are saying</h6>
        <b-row>
          <b-col v-for="(quote, index) in displayedQuotes" :key="index" md="4" class="mb-3">
            <div class="quote-card p-3 bg-light rounded h-100">
              <p class="mb-0 fst-italic">"{{ quote }}"</p>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Empty State -->
      <p v-if="!hasSentimentData && !quotes.length" class="my-2 text-center text-muted">
        No impact data available yet.
      </p>
    </template>
  </sme-card>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import { toNumber } from '@/utils/common';
import { TIME_SERIES } from '@/utils/chart';

const loading = ref(true);
const timeDataset = ref(null);
const stressedDataset = ref({ values: [], name: 'Less stressed', lineColor: PaletteColors['core-1'] });
const controlDataset = ref({ values: [], name: 'More in control', lineColor: PaletteColors['core-2'] });
const employerDataset = ref({ values: [], name: 'Positive about employer', lineColor: PaletteColors['core-3'] });
const quotes = ref([]);

const { timeGroupDataset, datasetToTimeGroupedDataset } = useTimeGroupDataset(
  timeDataset,
  ref(TIME_SERIES.MONTH_TO_MONTH),
);

const timeLabels = computed(() => {
  if (!timeGroupDataset.value?.values?.length) {
    return { values: [] };
  }
  return { values: timeGroupDataset.value.values };
});

const timeGroupedStressedDataset = computed(() =>
  timeGroupDataset.value ? datasetToTimeGroupedDataset(stressedDataset) || { values: [] } : { values: [] },
);
const timeGroupedControlDataset = computed(() =>
  timeGroupDataset.value ? datasetToTimeGroupedDataset(controlDataset) || { values: [] } : { values: [] },
);
const timeGroupedEmployerDataset = computed(() =>
  timeGroupDataset.value ? datasetToTimeGroupedDataset(employerDataset) || { values: [] } : { values: [] },
);

const calculatePercentages = (positiveValues, totalValues) => {
  if (!positiveValues || !totalValues || positiveValues.length !== totalValues.length) {
    return [];
  }
  return positiveValues.map((positive, i) => {
    const total = totalValues[i] || 0;
    return total > 0 ? Math.round((positive / total) * 1000) / 10 : 0;
  });
};

const calculateAverage = values => {
  if (!values?.length) {
    return 0;
  }
  const sum = values.reduce((acc, val) => acc + (val || 0), 0);
  return Math.round((sum / values.length) * 10) / 10;
};

const avgStressedPercent = computed(() => calculateAverage(timeGroupedStressedDataset.value?.values));
const avgControlPercent = computed(() => calculateAverage(timeGroupedControlDataset.value?.values));
const avgEmployerPercent = computed(() => calculateAverage(timeGroupedEmployerDataset.value?.values));

const hasSentimentData = computed(() => {
  return (
    (timeGroupedStressedDataset.value?.values?.length || 0) > 0 ||
    (timeGroupedControlDataset.value?.values?.length || 0) > 0 ||
    (timeGroupedEmployerDataset.value?.values?.length || 0) > 0
  );
});

const displayedQuotes = computed(() => quotes.value.slice(0, 6));

const getImpactData = async () => {
  loading.value = true;

  try {
    const timeSeries = 'm2m';

    const [stressResponse, controlResponse, employerResponse, quotesResponse] = await Promise.all([
      ApiClient.getPortalSentimentStress(timeSeries),
      ApiClient.getPortalSentimentControl(timeSeries),
      ApiClient.getPortalSentimentEmployer(timeSeries),
      ApiClient.getPortalQuotes(),
    ]);

    // Process stress data - API returns less_stress and total_responses
    if (stressResponse?.results?.length) {
      const time = stressResponse.results.find(result => result.id === 'time');
      const lessStress = stressResponse.results.find(result => result.id === 'less_stress');
      const totalResponses = stressResponse.results.find(result => result.id === 'total_responses');

      if (time) {
        timeDataset.value = time;
      }

      if (lessStress && totalResponses) {
        const percentages = calculatePercentages(lessStress.values, totalResponses.values);
        stressedDataset.value = {
          values: percentages,
          name: 'Less stressed',
          lineColor: PaletteColors['core-1'],
        };
      }
    }

    // Process control data - API returns more_control and total_responses
    if (controlResponse?.results?.length) {
      const moreControl = controlResponse.results.find(result => result.id === 'more_control');
      const totalResponses = controlResponse.results.find(result => result.id === 'total_responses');

      if (moreControl && totalResponses) {
        const percentages = calculatePercentages(moreControl.values, totalResponses.values);
        controlDataset.value = {
          values: percentages,
          name: 'More in control',
          lineColor: PaletteColors['core-2'],
        };
      }
    }

    // Process employer data - API returns positive_sentiment and total_responses
    if (employerResponse?.results?.length) {
      const positiveSentiment = employerResponse.results.find(result => result.id === 'positive_sentiment');
      const totalResponses = employerResponse.results.find(result => result.id === 'total_responses');

      if (positiveSentiment && totalResponses) {
        const percentages = calculatePercentages(positiveSentiment.values, totalResponses.values);
        employerDataset.value = {
          values: percentages,
          name: 'Positive about employer',
          lineColor: PaletteColors['core-3'],
        };
      }
    }

    // Process quotes - API returns array of strings
    if (quotesResponse?.data?.length) {
      quotes.value = quotesResponse.data;
    }
  } catch (error) {
    console.error('Failed to fetch impact data:', error);
  }

  loading.value = false;
};

onBeforeMount(() => getImpactData());
</script>

<style scoped>
.quote-card {
  border-left: 3px solid var(--core-3);
}

.quotes-section h6 {
  color: var(--gray-700);
  font-weight: 600;
}
</style>
